import { Box, ButtonBase, Typography, alpha } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { Check } from "@mui/icons-material";
import { ReactNode } from "react";

export interface OptionCardItem {
  value: string;
  label: ReactNode;
  /** Descripción bajo el label; sin ella la card solo muestra el label (ej. tipos de equipo de Mercader). */
  sub?: ReactNode;
  icon: ReactNode;
  /** data-testid de la card, para pruebas automatizadas. */
  testId?: string;
  /** Opción que ya no se puede elegir (p. ej. la vía no elegida en `DecisionIngreso`). Solo `layout="list"`. */
  disabled?: boolean;
}

/**
 * - `list`: una opción por fila, ícono cuadrado de 40 px y check a la derecha. Ej. `DecisionIngreso`.
 * - `grid`: filas en `columns` columnas, ícono de 44 px y check circular. Ej. cotizadores v2 (antes `RadioCardGroupCotizador`).
 * - `tiles`: cuadrícula automática apilada (ícono → texto), ícono de 32 px. Ej. tipos de equipo de Mercader (antes `RadioCardGridCotizador`).
 * - `stack`: pocas opciones en `columns` columnas, ícono cuadrado arriba sin check. Ej. tipo de garantía de Pymio.
 *
 * `grid`, `tiles` y `list` admiten `iconShape="square"|"round"` para alternar entre `IconCotizadorSquare` e `IconCotizadorRound`.
 * El default es `"square"` en `list` y `"round"` en `grid`/`tiles`.
 */
export type OptionCardLayout = "list" | "grid" | "tiles" | "stack";

/** Opción de `layout="check"`: solo el label, sin ícono ni descripción. */
export interface OptionCheckItem {
  value: string;
  label: string;
  /** Tooltip de la tarjeta (ej. el rango de monto del producto en el cotizador de Pymio). */
  title?: string;
  testId?: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: OptionCardItem[];
  layout?: OptionCardLayout;
  /** Columnas de `layout="grid"` y `layout="stack"`. */
  columns?: number;
  /** Forma del ícono en `list`, `grid` y `tiles`. Por defecto `"round"` en `grid`/`tiles`, `"square"` en `list`. */
  iconShape?: "round" | "square";
  /** Oculta el `CheckCotizador` en `grid` y `tiles`. Útil cuando el estado seleccionado se comunica solo con color. */
  hideCheck?: boolean;
}

/**
 * `check`: selección múltiple con tarjetas compactas y check redondo a la izquierda.
 * La selección no se puede vaciar: la última opción marcada no se desmarca.
 * Ej. productos del cotizador de Pymio (`.radio-card-solo` + `.radio-card-tick` del diseño).
 */
interface CheckProps {
  layout: "check";
  value: string[];
  onChange: (value: string[]) => void;
  options: OptionCheckItem[];
  /** Columnas en escritorio; en móvil quedan en dos. */
  columns: number;
}

/** Check circular relleno al activarse; usado en `grid` (22 px) y `tiles` (18 px). */
const CheckCotizador = ({ on, size, iconSize }: { on: boolean; size: number; iconSize: number }) => (
  <Box
    sx={{
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: on ? "primary.accent" : "tokens.surface2",
      border: "1.5px solid",
      borderColor: on ? "primary.accent" : "#D5DCE4",
      color: on ? "#fff" : "transparent",
    }}
  >
    <Check sx={{ fontSize: iconSize }} />
  </Box>
);

/** Ícono en cuadro redondeado (9 px). Default de `list`; opcional en `grid` y `tiles` vía `iconShape="square"`. SVG forzado al 50 % del `size`. */
const IconCotizadorSquare = ({ on, size = 40, children }: { on: boolean; size?: number; children: ReactNode }) => (
  <Box
    sx={(theme) => ({
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: "9px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      border: "1px solid",
      borderColor: on ? "primary.accent" : "divider",
      bgcolor: on ? "primary.accent" : "tokens.surface2",
      color: on ? "#FFF" : "tokens.textTertiary",
      boxShadow: on ? `0 4px 12px -2px ${alpha(theme.palette.primary.accent, 0.4)}` : "none",
      transition: "background-color .2s ease, color .2s ease, border-color .2s ease",
      "& > svg": { fontSize: size * 0.5 },
    })}
  >
    {children}
  </Box>
);

/** Ícono en círculo. Default de `grid` y `tiles`; opcional en `list` vía `iconShape="round"`. Sin sombra ni SVG forzado. */
const IconCotizadorRound = ({ on, size, children }: { on: boolean; size: number; children: ReactNode }) => (
  <Box
    sx={{
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: on ? "primary.accent" : "tokens.surface2",
      color: on ? "#fff" : "tokens.textTertiary",
      border: "1px solid",
      borderColor: on ? "primary.accent" : "divider",
      transition: "background-color .2s ease, color .2s ease",
    }}
  >
    {children}
  </Box>
);

/** Borde, fondo, sombra inset y hover compartidos por `grid`, `tiles` y `stack`. */
const cardCotizadorSx = (on: boolean) => ({
  border: "1.5px solid",
  borderColor: on ? "primary.accent" : "divider",
  borderRadius: "8px",
  bgcolor: on ? "primary.base" : "background.paper",
  boxShadow: (theme: Theme) => (on ? `0 0 0 1px ${theme.palette.primary.accent} inset` : "none"),
  transition: "border-color .18s ease, background-color .18s ease",
  "&:hover": { borderColor: "primary.accent", bgcolor: "primary.base" },
});

const OptionCardGrid = ({ value, onChange, options, columns = 1, iconShape = "round", hideCheck = false }: Props) => (
  <Box role="radiogroup" sx={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 1.5 }}>
    {options.map((opt) => {
      const on = value === opt.value;
      return (
        <ButtonBase
          key={opt.value}
          role="radio"
          aria-checked={on}
          onClick={() => onChange(opt.value)}
          data-testid={opt.testId}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            textAlign: "left",
            gap: 2,
            p: "18px 20px",
            ...cardCotizadorSx(on),
          }}
        >
          {iconShape === "square" ? (
            <IconCotizadorSquare on={on} size={44}>
              {opt.icon}
            </IconCotizadorSquare>
          ) : (
            <IconCotizadorRound on={on} size={44}>
              {opt.icon}
            </IconCotizadorRound>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
              {opt.label}
            </Typography>
            {opt.sub && (
              <Typography variant="body2" color="tokens.textTertiary" sx={{ mt: 0.25 }}>
                {opt.sub}
              </Typography>
            )}
          </Box>
          {!hideCheck && <CheckCotizador on={on} size={22} iconSize={14} />}
        </ButtonBase>
      );
    })}
  </Box>
);

/** `tiles`: cuadrícula automática — 4 por fila en escritorio, 2 en móvil (`minmax(150px, 1fr)`). Ícono de 32 px. */
const OptionCardTiles = ({ value, onChange, options, iconShape = "round", hideCheck = false }: Props) => (
  <Box
    role="radiogroup"
    sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.5 }}
  >
    {options.map((opt) => {
      const on = value === opt.value;
      return (
        <ButtonBase
          key={opt.value}
          role="radio"
          aria-checked={on}
          onClick={() => onChange(opt.value)}
          data-testid={opt.testId}
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            textAlign: "left",
            gap: "9px",
            p: "13px 14px 14px",
            ...cardCotizadorSx(on),
          }}
        >
          {iconShape === "square" ? (
            <IconCotizadorSquare on={on} size={32}>
              {opt.icon}
            </IconCotizadorSquare>
          ) : (
            <IconCotizadorRound on={on} size={32}>
              {opt.icon}
            </IconCotizadorRound>
          )}
          <Box sx={{ width: "100%", pr: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: "primary.dark" }}>
              {opt.label}
            </Typography>
            {opt.sub && (
              <Typography
                variant="body3"
                color="tokens.textTertiary"
                sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}
              >
                {opt.sub}
              </Typography>
            )}
          </Box>
          {!hideCheck && (
            <Box sx={{ position: "absolute", top: 11, right: 11, display: "flex" }}>
              <CheckCotizador on={on} size={18} iconSize={12} />
            </Box>
          )}
        </ButtonBase>
      );
    })}
  </Box>
);

/** `stack`: ícono cuadrado (40 px, `IconCotizadorSquare` inline) arriba, label y sub debajo, sin check. Ej. tipo de garantía de Pymio. */
const OptionCardStack = ({ value, onChange, options, columns = 2 }: Props) => (
  <Box
    role="radiogroup"
    sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: `repeat(${columns}, 1fr)` }, gap: 1.5 }}
  >
    {options.map((opt) => {
      const on = value === opt.value;
      return (
        <ButtonBase
          key={opt.value}
          role="radio"
          aria-checked={on}
          onClick={() => onChange(opt.value)}
          data-testid={opt.testId}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textAlign: "left",
            p: "14px 16px 16px",
            ...cardCotizadorSx(on),
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              mb: 2,
              borderRadius: "9px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid",
              borderColor: on ? "primary.accent" : "divider",
              bgcolor: on ? "primary.accent" : "tokens.surface2",
              color: on ? "#fff" : "tokens.textTertiary",
              transition: "background-color .2s ease, color .2s ease, border-color .2s ease",
              "& > svg": { fontSize: 20 },
            }}
          >
            {opt.icon}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.dark" }}>
            {opt.label}
          </Typography>
          {opt.sub && (
            <Typography variant="body3" color="tokens.textTertiary" sx={{ mt: 0.25 }}>
              {opt.sub}
            </Typography>
          )}
        </ButtonBase>
      );
    })}
  </Box>
);

const OptionCardCheck = ({ value, onChange, options, columns }: CheckProps) => {
  // Preserva el orden de `options` al agregar: la selección refleja el orden del array, no el de los clics.
  const toggle = (opt: string) => {
    if (value.includes(opt)) {
      if (value.length > 1) onChange(value.filter((v) => v !== opt));
      return;
    }
    onChange(options.map((o) => o.value).filter((v) => v === opt || value.includes(v)));
  };

  return (
    <Box
      role="group"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`,
          md: `repeat(${columns}, minmax(0, 1fr))`,
        },
        gap: "10px",
      }}
    >
      {options.map((opt) => {
        const on = value.includes(opt.value);
        return (
          <ButtonBase
            key={opt.value}
            role="checkbox"
            aria-checked={on}
            onClick={() => toggle(opt.value)}
            title={opt.title}
            data-testid={opt.testId}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              textAlign: "left",
              gap: "10px",
              minHeight: 50,
              p: "10px 12px",
              border: "1.5px solid",
              borderColor: on ? "primary.accent" : "divider",
              borderRadius: "10px",
              bgcolor: (theme) => (on ? alpha(theme.palette.primary.accent, 0.04) : theme.palette.background.paper),
              transition: "border-color .18s ease, background-color .18s ease",
              "&:hover": { borderColor: on ? "primary.accent" : "tokens.borderStrong" },
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: 16,
                height: 16,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1.5px solid",
                borderColor: on ? "primary.accent" : "tokens.borderStrong",
                bgcolor: on ? "primary.accent" : "background.paper",
                color: "#fff",
                transition: "background-color .15s ease, border-color .15s ease",
              }}
            >
              {on && <Check sx={{ fontSize: 10 }} />}
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: on ? 600 : 500, lineHeight: 1.25, color: "primary.dark", textWrap: "balance" }}
            >
              {opt.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

/**
 * `list`: una opción por fila, ícono de 40 px (square por defecto) y check circular a la derecha.
 * Soporta `disabled` por ítem. Convive con `RadioCardGroup` (spec viejo, sin tile ni check).
 */
const OptionCardList = ({ value, onChange, options, iconShape = "square" }: Props) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
    {options.map((opt) => {
      const on = value === opt.value;
      return (
        <Box
          key={opt.value}
          onClick={() => !opt.disabled && onChange(opt.value)}
          data-testid={opt.testId}
          aria-disabled={opt.disabled}
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            gap: "16px",
            p: "16px 18px",
            borderRadius: "10px",
            border: "1.5px solid",
            borderColor: on ? "primary.accent" : "divider",
            bgcolor: on ? "primary.base" : "background.paper",
            // El inset simula el borde de 2px del estado activo sin recalcular el layout.
            boxShadow: on ? `inset 0 0 0 1px ${theme.palette.primary.accent}` : "none",
            cursor: opt.disabled ? "not-allowed" : "pointer",
            opacity: opt.disabled ? 0.5 : 1,
            userSelect: "none",
            transition: "border-color .18s ease, background-color .18s ease, box-shadow .2s ease",
            "&:hover": opt.disabled
              ? {}
              : {
                  borderColor: on ? "primary.accent" : alpha(theme.palette.primary.accent, 0.7),
                  bgcolor: on ? "primary.base" : alpha(theme.palette.primary.accent, 0.06),
                },
          })}
        >
          {iconShape === "round" ? (
            <IconCotizadorRound on={on} size={40}>
              {opt.icon}
            </IconCotizadorRound>
          ) : (
            <IconCotizadorSquare on={on}>{opt.icon}</IconCotizadorSquare>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
              {opt.label}
            </Typography>
            {opt.sub && (
              <Typography sx={{ fontSize: 12.5, color: "tokens.textTertiary", mt: "2px", lineHeight: 1.5 }}>
                {opt.sub}
              </Typography>
            )}
          </Box>

          <CheckCotizador on={on} size={22} iconSize={14} />
        </Box>
      );
    })}
  </Box>
);

export const OptionCardGroup = (props: Props | CheckProps) => {
  if (props.layout === "check") return <OptionCardCheck {...props} />;
  if (props.layout === "grid") return <OptionCardGrid {...props} />;
  if (props.layout === "tiles") return <OptionCardTiles {...props} />;
  if (props.layout === "stack") return <OptionCardStack {...props} />;

  return <OptionCardList {...props} />;
};
